import { ejsRoms, ejsSystemOf, ejsSystemOfName, isRetiredGame, zipEntryNames } from "../emulator/ejs";
import { desktop, isDesktop, isNative } from "../desktop/desktop";
import { readIdbfs, writeIdbfsFiles } from "./idbfs";

// The home page lists games without booting an emulator core: mGBA's from
// its IDBFS mount, N64 ones from EmulatorJS's own store. PlayStation and PSP
// games stored before those were dropped are left out. The desktop app adds
// the PlayStation, PSP and Xbox games in its games folder.

export type System = "gb" | "gbc" | "gba" | "n64" | "psx" | "psp" | "xbox";

export interface LocalGame {
  name: string;
  system: System;
}

export const SYSTEM_LABEL: Record<System, string> = {
  gb: "Game Boy",
  gbc: "Game Boy Color",
  gba: "Game Boy Advance",
  n64: "Nintendo 64",
  psx: "PlayStation",
  psp: "PSP",
  xbox: "Xbox",
};

/** Every console, in the order they're listed. The website lists the disc ones (their cloud games) but plays them only in the desktop app. */
export const SYSTEM_ORDER: System[] = ["gb", "gbc", "gba", "n64", "psx", "psp", "xbox"];

/** Whether this build plays games for `system`. */
export const playsHere = (system: string | null): boolean => isDesktop || !isNative(system);

const IDBFS_DB = "/data";
const GAMES_PREFIX = "/data/games/";

export function systemOf(name: string): System {
  const ejs = ejsSystemOfName(name);
  if (ejs) return ejs;
  const ext = name.slice(name.lastIndexOf(".") + 1).toLowerCase();
  if (ext === "gb") return "gb";
  if (ext === "gbc") return "gbc";
  return "gba";
}

// A zip's system comes from the files inside it (its central directory), so
// a folder entry first doesn't hide the game. Anything unreadable counts as
// GBA. An N64 zip can sit in mGBA's store if it was added before this could
// tell; it lists as N64 and moves to EmulatorJS when played (useEmulator).
async function systemOfStored(name: string, bytes: Uint8Array): Promise<System> {
  const blob = new Blob([bytes as BlobPart]);
  const ejs = await ejsSystemOf(name, blob);
  if (ejs) return ejs;
  if (!/\.zip$/i.test(name)) return systemOf(name);
  const rom = (await zipEntryNames(blob)).find((entry) => /\.(gb|gbc|gba)$/i.test(entry));
  return rom ? systemOf(rom) : "gba";
}

async function listMgbaGames(): Promise<LocalGame[]> {
  const files = (await readIdbfs(IDBFS_DB, GAMES_PREFIX)).filter(
    (f) => !f.path.slice(GAMES_PREFIX.length).includes("/"),
  );
  const games = await Promise.all(
    files.map(async ({ name, bytes }) => {
      const blob = new Blob([bytes as BlobPart]);
      return (await isRetiredGame(name, blob)) ? null : { name, system: await systemOfStored(name, bytes) };
    }),
  );
  return games.filter((game): game is LocalGame => game !== null);
}

async function listEjsGames(): Promise<LocalGame[]> {
  const names = await ejsRoms.list().catch(() => [] as string[]);
  const games = await Promise.all(
    names.map(async (name) =>
      (await isRetiredGame(name, (await ejsRoms.get(name)) ?? null))
        ? null
        : { name, system: (await ejsRoms.system(name)) as System },
    ),
  );
  return games.filter((game): game is LocalGame => game !== null);
}

/** The file types a folder scan looks at in the browser: cartridges and archives of them. */
export const SCANNED = /\.(gb|gbc|gba|n64|z64|v64|zip)$/i;
const CARTRIDGE = /\.(gb|gbc|gba)$/i;

/**
 * Stores games found in a scanned folder, as adding each would, without
 * starting the player: N64 in EmulatorJS's store, Game Boy in mGBA's. Files
 * that aren't such games (a zip of anything else, a PlayStation disc) are
 * skipped. Returns the names stored.
 */
export async function storeGames(files: File[]): Promise<string[]> {
  const known = new Set((await listLocalGames().catch(() => [])).map((game) => game.name));
  const stored: string[] = [];
  const mgba: [string, Uint8Array][] = [];
  for (const file of files) {
    // Discs and archives of them are far bigger than any cartridge.
    if (known.has(file.name) || file.size > 128 * 1024 * 1024 || (await isRetiredGame(file.name, file))) continue;
    if (await ejsSystemOf(file.name, file)) {
      await ejsRoms.put(file.name, file);
    } else if (
      CARTRIDGE.test(file.name) ||
      (/\.zip$/i.test(file.name) && (await zipEntryNames(file)).some((entry) => CARTRIDGE.test(entry)))
    ) {
      mgba.push([GAMES_PREFIX + file.name, new Uint8Array(await file.arrayBuffer())]);
    } else continue;
    known.add(file.name);
    stored.push(file.name);
  }
  if (mgba.length) await writeIdbfsFiles(IDBFS_DB, [GAMES_PREFIX.slice(0, -1)], mgba);
  return stored;
}

/** A game's file as this browser holds it, for sending to the cloud library. */
export async function readLocalRom(name: string): Promise<File | null> {
  const blob = await ejsRoms.get(name).catch(() => undefined);
  if (blob) return new File([blob], name);
  const stored = (await readIdbfs(IDBFS_DB, GAMES_PREFIX + name)).find((f) => f.path === GAMES_PREFIX + name);
  return stored ? new File([stored.bytes as BlobPart], name) : null;
}

export async function listLocalGames(): Promise<LocalGame[]> {
  const [mgba, ejs, native] = await Promise.all([
    listMgbaGames(),
    listEjsGames(),
    isDesktop ? desktop.listGames().then((games) => games.map(({ name, system }) => ({ name, system }))) : [],
  ]);
  const games = [...mgba, ...ejs, ...native];
  return games.sort(
    (a, b) =>
      SYSTEM_ORDER.indexOf(a.system) - SYSTEM_ORDER.indexOf(b.system) ||
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
}
